import React, { Suspense } from 'react';
import { WrapperWidget } from '@agentselly/ui';
import { useTranslate } from '@tolgee/react';
import { FileText, Download, ArrowRight } from 'lucide-react';
import { AppLink } from '~/components/AppLink';
import { useAppParams } from '~/helper/useAppParams';

const PDFThumbnail = React.lazy(() =>
  import('~/components/pdf/PDFThumbnail').then((m) => ({
    default: m.PDFThumbnail,
  })),
);

interface Document {
  readonly id: string;
  readonly type: string;
  readonly title: string;
  readonly filename: string;
  readonly url: string;
  readonly isPrimary?: boolean;
}

interface Props {
  readonly exposeUrl?: string;
  readonly status?: string | null;
  readonly documents?: Document[];
  readonly link?: string;
  readonly photos?: readonly string[];
  readonly propertyId?: string;
}

export function CockpitExposeWidget(props: Props) {
  const { t } = useTranslate();
  const params = useAppParams();

  const documents = props.documents || [];

  // Fix URLs to use appropriate proxy based on file type
  const fixedDocuments = (documents || []).map((doc) => {
    const isPdf =
      doc?.filename?.toLowerCase().endsWith('.pdf') ||
      doc?.url?.toLowerCase().includes('.pdf');

    // Already has correct proxy
    if (doc?.url?.includes('/pdf-proxy/') || doc?.url?.includes('/assets/')) {
      return doc;
    }

    // If URL is from secureassets.agentselly or contains /property/, convert to proxy URL
    if (
      doc?.url?.includes('secureassets.agentselly') ||
      doc?.url?.includes('/property/')
    ) {
      const urlParts = doc.url.split('/');
      const pathIndex = urlParts.findIndex((part) => part === 'property');
      if (pathIndex !== -1) {
        const assetPath = urlParts.slice(pathIndex).join('/');
        const propertyIdFromUrl = urlParts[pathIndex + 1];
        const propId =
          props.propertyId || params.propertyId || propertyIdFromUrl || '';

        // PDFs use pdf-proxy with full route, images use /assets/
        const proxyUrl = isPdf
          ? `/${params.lang || 'de'}/${params.companyId}/${
              params.contactId || ''
            }/cockpit/${propId}/pdf-proxy/${assetPath}`
          : `/assets/${assetPath}`;

        return { ...doc, url: proxyUrl };
      }
    }

    return doc;
  });

  // Helper to determine if a document is a valid image (and not a PDF or non-image)
  const isImageDoc = (doc: Document): boolean => {
    const fn = (doc.filename || '').toLowerCase().split('?')[0].split('#')[0];
    const u = (doc.url || '').toLowerCase().split('?')[0].split('#')[0];
    if (
      fn.endsWith('.pdf') ||
      u.endsWith('.pdf') ||
      u.includes('/pdf-proxy/')
    ) {
      return false;
    }
    if (doc.type === 'photo' || doc.type === 'image') {
      return true;
    }
    return (
      /\.(jpe?g|png|webp|gif|avif)$/i.test(fn) ||
      /\.(jpe?g|png|webp|gif|avif)$/i.test(u)
    );
  };

  const cleanPhotos = (props.photos || []).filter(
    (p): p is string =>
      typeof p === 'string' &&
      p.trim().length > 0 &&
      !p.trim().toLowerCase().split('?')[0].split('#')[0].endsWith('.pdf'),
  );

  // Extract photos from documents or use props.photos
  const photoUrls =
    cleanPhotos.length > 0
      ? cleanPhotos
      : fixedDocuments
          .filter(isImageDoc)
          .slice(0, 4)
          .map((doc) => doc.url);

  const primaryDocs = (fixedDocuments || []).filter((d) => d?.isPrimary);
  const otherDocs = (fixedDocuments || []).filter((d) => {
    // Exclude primary docs
    if (d?.isPrimary) return false;

    // Exclude photos/images as they are shown in the photo grid
    // Also explicitly exclude 'Hauptfoto'
    if (d.title === 'Hauptfoto' || d.title === 'Main photo' || isImageDoc(d)) {
      return false;
    }

    return true;
  });

  // Sort primary docs: Expose first
  const sortedPrimaryDocs = [...primaryDocs].sort((a, b) => {
    const isExposeA =
      a.type === 'expose' ||
      (a.title ?? '').toLowerCase().includes('verkaufsdokumentation') ||
      (a.title ?? '').toLowerCase().includes('exposé');
    const isExposeB =
      b.type === 'expose' ||
      (b.title ?? '').toLowerCase().includes('verkaufsdokumentation') ||
      (b.title ?? '').toLowerCase().includes('exposé');

    if (isExposeA && !isExposeB) return -1;
    if (!isExposeA && isExposeB) return 1;
    return 0;
  });

  // Check if there are any expose or flyer documents
  const exposeFlyerDocs = [
    ...sortedPrimaryDocs,
    ...otherDocs,
  ].filter((d) => d?.type === 'expose' || d?.type === 'flyer');

  // Check if exposeUrl is a valid document URL (not just generic domain)
  const isValidExposeUrl =
    props.exposeUrl &&
    props.exposeUrl.trim() !== '' && // Not empty
    props.exposeUrl.length > 30 && // Must be longer than just a domain
    (props.exposeUrl.includes('/property/') ||
      props.exposeUrl.endsWith('.pdf')) && // Must contain property path or be a PDF
    !props.exposeUrl.match(/^https?:\/\/[^/]+\/?$/); // Not just a domain with optional trailing slash

  const hasExposeOrFlyer =
    exposeFlyerDocs.length > 0 || Boolean(isValidExposeUrl);

  // Use the link from backend props instead of building it manually
  const documentsRoute = props.link || '#';

  const getStatusColor = (status?: string | null) => {
    switch ((status ?? '').toLowerCase()) {
      case 'online':
        return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border-green-200';
      case 'offline':
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 dark:text-gray-200 border-gray-200 dark:border-gray-700';
      case 'pending':
        return 'bg-yellow-100 dark:bg-amber-900/30 text-yellow-800 dark:text-amber-400 border-yellow-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  return (
    <WrapperWidget>
      <div className="h-full flex flex-col gap-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold">
            {t('cockpit_expose_widget_title')}
          </h2>
          {props.status ? (
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full border ${getStatusColor(
                props.status,
              )}`}
            >
              {t(`cockpit_expose_widget_status_${props.status}`)}
            </span>
          ) : null}
        </div>

        {hasExposeOrFlyer ? (
          <>
            {sortedPrimaryDocs.length > 0 ? (
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                {sortedPrimaryDocs.slice(0, 2).map((doc) => {
                  const isExpose =
                    doc.type === 'expose' ||
                    (doc.title ?? '')
                      .toLowerCase()
                      .includes('verkaufsdokumentation') ||
                    (doc.title ?? '').toLowerCase().includes('exposé');

                  const aspectClass = isExpose
                    ? 'aspect-[1.414]'
                    : 'aspect-[0.707]';
                  const widthClass = isExpose
                    ? 'w-full sm:flex-[2]'
                    : 'w-full sm:flex-1';
                  const singleDocClass =
                    sortedPrimaryDocs.length === 1 && !isExpose
                      ? 'sm:max-w-[50%] mx-auto'
                      : '';

                  return (
                    <a
                      key={doc.id}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`group relative rounded-lg overflow-hidden border border-primary-200 hover:border-primary-400 transition-all hover:shadow-md dark:hover:shadow-gray-900/50 bg-white dark:bg-gray-800 dark:bg-gray-900 ${aspectClass} ${widthClass} ${singleDocClass}`}
                      data-track="immodossier cockpit expose document"
                    >
                      <Suspense
                        fallback={
                          <div className="w-full h-full bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col items-center justify-center p-3">
                            <div className="w-16 h-16 mb-3 rounded-lg bg-white dark:bg-gray-800/80 dark:bg-gray-700/80 flex items-center justify-center shadow-sm dark:shadow-gray-900/50 animate-pulse">
                              <FileText className="h-10 w-10 text-primary-600" />
                            </div>
                          </div>
                        }
                      >
                        <PDFThumbnail
                          url={doc.url}
                          width={600}
                          className="w-full h-full flex items-center justify-center bg-gray-50 dark:bg-gray-800 [&_canvas]:!w-auto [&_canvas]:!h-auto [&_canvas]:!max-w-full [&_canvas]:!max-h-full"
                          fallback={
                            <div className="w-full h-full bg-gradient-to-br from-primary-50 to-primary-100 flex flex-col items-center justify-center p-3">
                              <div className="w-16 h-16 mb-3 rounded-lg bg-white dark:bg-gray-800/80 dark:bg-gray-700/80 flex items-center justify-center shadow-sm dark:shadow-gray-900/50">
                                <FileText className="h-10 w-10 text-primary-600" />
                              </div>
                              <p className="font-semibold text-sm text-center text-primary-900">
                                {doc.title}
                              </p>
                            </div>
                          }
                        />
                      </Suspense>
                      <div className="absolute inset-0 bg-gradient-to-t from-primary-600/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        <Download className="h-8 w-8 mb-2 drop-shadow-md" />
                        <p className="font-semibold text-sm drop-shadow-md">
                          {t('cockpit_expose_widget_download')}
                        </p>
                      </div>
                    </a>
                  );
                })}
              </div>
            ) : isValidExposeUrl ? (
              <a
                href={props.exposeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative aspect-[1.414] w-full rounded-lg overflow-hidden border border-primary-200 hover:border-primary-400 transition-all hover:shadow-md dark:hover:shadow-gray-900/50 bg-gradient-to-br from-primary-50 to-primary-100"
                data-track="immodossier cockpit expose document"
              >
                <div className="w-full h-full flex flex-col items-center justify-center p-3">
                  <div className="w-12 h-12 mb-2 rounded-lg bg-white dark:bg-gray-800/80 dark:bg-gray-700/80 flex items-center justify-center shadow-sm dark:shadow-gray-900/50">
                    <FileText className="h-7 w-7 text-primary-600" />
                  </div>
                  <p className="font-semibold text-xs text-center text-primary-900">
                    Exposé
                  </p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-primary-600/90 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <Download className="h-6 w-6 mb-1 drop-shadow-md" />
                  <p className="font-semibold text-xs drop-shadow-md">
                    {t('cockpit_expose_widget_download')}
                  </p>
                </div>
              </a>
            ) : null}
          </>
        ) : (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400">
            <FileText className="mx-auto h-10 w-10 mb-2 text-gray-400 dark:text-gray-500 dark:text-gray-400" />
            <p className="text-sm">
              {t('cockpit_expose_widget_no_expose_flyer')}
            </p>
          </div>
        )}

        {hasExposeOrFlyer && otherDocs.length > 0 && (
          <div className="space-y-1 pt-2 border-t">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {t('cockpit_expose_widget_other_documents')}
            </p>
            {otherDocs.slice(0, 2).map((doc) => (
              <a
                key={doc.id}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-1.5 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800 dark:bg-gray-800 dark:hover:bg-gray-800 transition-colors group text-xs"
                data-track="immodossier cockpit expose download"
              >
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <FileText className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 dark:text-gray-400 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-200 dark:text-gray-300 truncate">
                    {doc.title}
                  </span>
                </div>
                <Download className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 dark:text-gray-400 group-hover:text-primary-600 flex-shrink-0 transition-colors" />
              </a>
            ))}
          </div>
        )}

        <AppLink
          to={documentsRoute}
          className="text-xs text-primary-600 hover:text-primary-700 flex items-center gap-1 transition-colors pt-2"
          data-track="immodossier cockpit expose view all"
        >
          {t('cockpit_expose_widget_view_all')}
          <ArrowRight className="h-3.5 w-3.5" />
        </AppLink>

        {/* Property Photos */}
        {photoUrls.length > 0 && (
          <div className="mt-auto pt-3 border-t">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              Objektfotos
            </p>
            <div className="grid grid-cols-4 gap-2">
              {photoUrls.slice(0, 4).map((photo, index) => (
                <a
                  key={index}
                  href={photo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="aspect-square rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 block cursor-pointer"
                  data-track="immodossier cockpit expose photo"
                >
                  <img
                    src={photo}
                    alt={`Foto ${index + 1}`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                    onError={(e) => {
                      e.currentTarget.parentElement!.style.display = 'none';
                    }}
                  />
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </WrapperWidget>
  );
}
